import * as React from "react";
import {
    gql,
    useLazyQuery
} from "@apollo/client";
import {
    GetCommunityPointQueryQuery,
    GetCommunityPointQueryQueryVariables
} from "../../../schemaTypes";
import { NormalizedErrorMap } from "../../../types/NormalizedErrorMap";
import { normalizeErrors } from "../../../utils/normalizeErrors";

export const getCommunityPointQuery = gql`
  query GetCommunityPointQuery($communityId: String!) {
    getCommunityPoint(communityId: $communityId) {
      errors {
        path
        message
      }
      message
      data {
        memberCommunityPoint
        partnerCommunityExchangeRate
      }
    }
  }
`;

export type MemberCommunityPoint = NonNullable<
  NonNullable<GetCommunityPointQueryQuery["getCommunityPoint"]>["data"]
>;

export interface GetCommunityPointResult {
  errors: NormalizedErrorMap | null;
  message?: string;
  data?: MemberCommunityPoint;
  supportMeta?: {
    code?: string;
    supportRef?: string;
  };
}

export interface UseGetCommunityPointOptions {
  // Fetch as soon as the component mounts, and again whenever communityId changes.
  fetchOnMount?: boolean;
}

const fromResponse = (
  response: GetCommunityPointQueryQuery["getCommunityPoint"] | undefined
): GetCommunityPointResult => {
  if (!response) {
    return { errors: { general: "No response from server." } };
  }

  return {
    errors: response.errors ? normalizeErrors(response.errors) : null,
    message: response.message ?? undefined,
    data: response.data ?? undefined,
  };
};

// Most failures (not a member, no exchange rate, CBM unavailable) are thrown, not returned.
const fromApolloError = (err: unknown): GetCommunityPointResult => {
  const maybeApolloErr = err as {
    message?: string;
    graphQLErrors?: ReadonlyArray<{ message?: string; extensions?: { code?: unknown; supportRef?: unknown } }>;
  };

  const firstGraphQLError = maybeApolloErr.graphQLErrors?.[0];
  const code = firstGraphQLError?.extensions?.code;
  const supportRef = firstGraphQLError?.extensions?.supportRef;
  const message =
    firstGraphQLError?.message ||
    maybeApolloErr.message ||
    "Unable to fetch your community points right now. Please try again.";

  return {
    errors: { general: message },
    supportMeta: {
      ...(typeof code === "string" ? { code } : {}),
      ...(typeof supportRef === "string" ? { supportRef } : {}),
    },
  };
};

export function useGetCommunityPoint(
  communityId?: string,
  { fetchOnMount = true }: UseGetCommunityPointOptions = {}
) {
  const [runQuery, { data, loading, error, called }] = useLazyQuery<
    GetCommunityPointQueryQuery,
    GetCommunityPointQueryQueryVariables
  >(getCommunityPointQuery, {
    // The balance lives with the third party, so always ask the server rather than the cache.
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  // For button clicks: defaults to the hook's communityId, or pass another one.
  const fetchCommunityPoint = React.useCallback(
    async (id: string | undefined = communityId): Promise<GetCommunityPointResult> => {
      if (!id) {
        return { errors: { general: "Select a community first." } };
      }

      try {
        const response = await runQuery({ variables: { communityId: id } });
        if (response.error) {
          return fromApolloError(response.error);
        }
        return fromResponse(response.data?.getCommunityPoint);
      } catch (err) {
        return fromApolloError(err);
      }
    },
    [runQuery, communityId]
  );

  React.useEffect(() => {
    if (fetchOnMount && communityId) {
      fetchCommunityPoint(communityId);
    }
  }, [fetchOnMount, communityId, fetchCommunityPoint]);

  const result = React.useMemo<GetCommunityPointResult | null>(() => {
    if (error) return fromApolloError(error);
    if (data) return fromResponse(data.getCommunityPoint);
    return null;
  }, [data, error]);

  return {
    communityPoint: result?.data,
    errors: result?.errors ?? null,
    message: result?.message,
    supportMeta: result?.supportMeta,
    fetchCommunityPoint,
    loading,
    called,
    error,
  };
}
